import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  CancellationPolicyStep,
  ConfirmationForm,
  ConfirmationMarketingLink,
  ConfirmationMessage,
} from 'src/modules/confirmation/components'
import type { ConfirmationErrorKind } from 'src/modules/confirmation/components/confirmation-message/confirmation-message.interface'
import { LATE_CANCEL_QUERY_PARAM } from 'src/modules/confirmation/constants/cancellation-policy.constants'
import {
  buildCancelBody,
  mapSubmitErrorToKind,
  mapSubmitErrorToPolicyStep,
  resolveLoadedState,
  resolveVisibleStep,
} from 'src/modules/confirmation/containers/confirmation-wrapper/confirmation-wrapper.utils'
import type { CancellationPolicyStepKind } from 'src/modules/confirmation/types/confirmation.types'
import { Loader, Typography } from 'src/modules/core/components'
import { useConfirmEvent } from 'src/modules/event/hooks/use-confirm-event'
import { useEventByUrlCode } from 'src/modules/event/hooks/use-event-by-url-code'
import classes from './confirmation-wrapper.module.scss'

import type { ConfirmationStatus } from './confirmation-wrapper.interface'

export function ConfirmationWrapper() {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const eventByUrlCode = useEventByUrlCode(params.urlCode)
  const confirmEvent = useConfirmEvent()

  const [status, setStatus] = useState<ConfirmationStatus>('pending')
  const [errorKind, setErrorKind] = useState<ConfirmationErrorKind>('load_failed')
  const [submittingAction, setSubmittingAction] = useState<'confirm' | 'cancel' | null>(null)
  const [policyStep, setPolicyStep] = useState<CancellationPolicyStepKind | null>(null)
  const lateCancelLinkHandled = useRef(false)
  const opensWarningOnLoad = searchParams.get(LATE_CANCEL_QUERY_PARAM) === '1'

  const handleConfirmEvent = useCallback(() => {
    if (!params.urlCode || confirmEvent.isLoading) return
    setSubmittingAction('confirm')
    confirmEvent.mutate(
      { url_code: params.urlCode, confirm: true },
      {
        onSuccess: () => {
          setStatus('success')
        },
        onError: (err) => {
          setStatus('error')
          setErrorKind(mapSubmitErrorToKind(err))
        },
        onSettled: () => {
          setSubmittingAction(null)
        },
      },
    )
  }, [confirmEvent, params.urlCode])

  // A 409 inside the window opens a step; the details are re-read for the fresh cutoff and text.
  const openPolicyStepFromError = useCallback(
    (err: unknown): boolean => {
      const step = mapSubmitErrorToPolicyStep(err)
      if (!step) return false
      setPolicyStep(step)
      confirmEvent.reset()
      void eventByUrlCode.refetch()
      return true
    },
    [confirmEvent, eventByUrlCode],
  )

  const submitCancel = useCallback(
    (acknowledgeLateCancel: boolean) => {
      if (!params.urlCode || confirmEvent.isLoading) return
      setSubmittingAction('cancel')
      confirmEvent.mutate(buildCancelBody(params.urlCode, acknowledgeLateCancel), {
        onSuccess: () => {
          if (acknowledgeLateCancel) setPolicyStep('late_done')
          else setStatus('canceled')
        },
        onError: (err) => {
          if (openPolicyStepFromError(err)) return
          setStatus('error')
          setErrorKind(mapSubmitErrorToKind(err))
        },
        onSettled: () => {
          setSubmittingAction(null)
        },
      })
    },
    [confirmEvent, params.urlCode, openPolicyStepFromError],
  )

  const handleCancelEvent = useCallback(() => submitCancel(false), [submitCancel])
  const handleCancelAnyway = useCallback(() => submitCancel(true), [submitCancel])
  const handleKeepAppointment = useCallback(() => setPolicyStep(null), [])

  useEffect(() => {
    if (confirmEvent.status === 'success' || confirmEvent.status === 'error') {
      return
    }

    if (!params.urlCode) {
      setStatus('error')
      setErrorKind('invalid_link')
      return
    }

    if (eventByUrlCode.isLoadingError || eventByUrlCode.isError) {
      setStatus('error')
      setErrorKind('load_failed')
      return
    }

    if (eventByUrlCode.isFetched && eventByUrlCode.data === null) {
      setStatus('error')
      setErrorKind('invalid_link')
      return
    }

    const data = eventByUrlCode.data
    if (!data) {
      return
    }

    const loadedState = resolveLoadedState(data)
    if (loadedState === 'late_cancelled') {
      setPolicyStep('late_done')
      return
    }

    if (loadedState === 'canceled') {
      setStatus('canceled')
      return
    }

    if (loadedState === 'event_past') {
      setStatus('error')
      setErrorKind('event_past')
      return
    }

    if (!lateCancelLinkHandled.current) {
      lateCancelLinkHandled.current = true
      if (opensWarningOnLoad && data.cancellation_policy?.state === 'late_allowed') setPolicyStep('warning')
    }

    setStatus('pending')
  }, [
    confirmEvent.status,
    eventByUrlCode.isLoadingError,
    eventByUrlCode.isError,
    eventByUrlCode.isFetched,
    eventByUrlCode.data,
    params.urlCode,
    opensWarningOnLoad,
  ])

  const visibleStep = resolveVisibleStep(status, policyStep)

  return (
    <div className={classes.root}>
      <Loader isLoading={eventByUrlCode.isFetching} className={classes.loader} full />
      {visibleStep && eventByUrlCode.data ? (
        <CancellationPolicyStep
          step={visibleStep}
          event={eventByUrlCode.data}
          onKeep={handleKeepAppointment}
          onCancelAnyway={handleCancelAnyway}
          onConfirm={handleConfirmEvent}
          submittingAction={submittingAction}
        />
      ) : status !== 'pending' ? (
        <ConfirmationMessage variant={status} errorKind={status === 'error' ? errorKind : undefined} />
      ) : (
        <ConfirmationForm
          event={eventByUrlCode.data}
          onConfirm={handleConfirmEvent}
          onCancel={handleCancelEvent}
          urlCode={params.urlCode ?? undefined}
          submittingAction={submittingAction}
        />
      )}

      <ConfirmationMarketingLink classes={classes} />
    </div>
  )
}
